import React, { useState, useMemo } from 'react'
import { Grid } from '@mui/material'
import { withTranslations } from 'widgets-shared/core'
import { compose } from 'widgets-shared/core'
import MarketLine from '../../components/MarketLine/MarketLine'
import OddsComparisonHeader from './OddsComparisonHeader/OddsComparisonHeader'
import TabBar from 'smartstream-widgets/controls/TabBar/TabBar'
import { homeFirstCompetitions } from "smartstream-widgets/constants/shared"
import { withComponentContext } from 'widgets-shared/core'
import { getHighestPriceInMarkets } from 'smartstream-widgets/utils/markets'

import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { Market, MarketSelectionType } from '../../types/market'
import { PropsComponent, Record } from './types'
import type { CompetitorWithFixtureData, Competitor } from 'smartstream-widgets/types/competitor'
import { arrayIsNotEmpty } from 'widgets-shared/utilities'
import { alpha  } from '@mui/material/styles'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'
import { withComponentContextProvider } from 'widgets-shared/core'

type CompetitorWithMarketSelection = {
  competitor?: CompetitorWithFixtureData,
  selection?: MarketSelectionType
}

type CompetitorsWithMarketSelection = {
  AWAY?: CompetitorWithMarketSelection,
  HOME?: CompetitorWithMarketSelection
}

const styles = (theme: Theme) => createStyles({
  linesContainer: {
    flexGrow: 1,
    paddingTop: 0,
    alignContent: 'unset'
  },
  linesContainerFewMarkets: {
    '& $marketOddLines': {
      margin: `${theme.spacing(2)} 0`
    }
  },
  marketOddLines: {
    padding: `0px 15px`,
    justifyContent: 'space-evenly',
    display: 'flex',
    gap: 4
  },
  root: {
    paddingBottom: 20,
    height: '100%'
  },
  tab: {
    borderBottom: `1px solid ${theme.palette.grey[500]}`,
    minHeight: 'auto',
    height: 24,
    '& span': {
      fontSize: 9,
      [theme.breakpoints.up(768)]: {
        fontSize: 11
      },
      [theme.breakpoints.up(900)]: {
        fontSize: 13
      },
      [theme.breakpoints.up(1440)]: {
        fontSize: 15
      }
    }
  },
  tabApp: {
    boxShadow: 'none',
    zIndex: 4
  },
  tabBar: {
    minHeight: 'auto',
    backgroundColor: theme.palette.background.default
  },
  tabFullWidth: {
    minWidth: 'auto',
    padding: 0
  },
  iconLink: {
    display: 'block',
    margin: 0,
    maxWidth: 24,
    padding: 0
  },
  iconLabel: {
    display: 'block',
    margin: 0,
    maxWidth: 24,
    padding: 0
  },
  affiliateText: {
    color: theme.palette.grey['700'],
    fontSize: 10,
    textAlign: 'center',
    [theme.breakpoints.up(900)]: {
      fontSize: 10
    },
    [theme.breakpoints.up(1440)]: {
      fontSize: 11
    },
    lineHeight: 1,
    '&:first-child': {
      marginBottom: 2
    },
    '&:last-child': {
      marginTop: 2
    }
  },
  bestSelection: {
    backgroundColor: alpha(theme.palette.success.main, 0.11),
    border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
    color: theme.palette.success.main,
    '&:hover': {
      background: theme.palette.success.main,
      '& $captionOdd': {
        color: theme.palette.common.white
      }
    },
    '&$buttonDisabled': {
      '& $captionOdd': {
        color: theme.palette.success.main
      }
    }
  },
  oddsComparisonBookmakerContainer: {
    width: 144,
    [theme.breakpoints.up(500)]: {
      width: 300
    }    
  },
  logo: {
    width: 62,
    height: 15,
    [theme.breakpoints.up(400)]: {
      width: 70,
      height: 15,
    },
    [theme.breakpoints.up(768)]: {
      width: 63,
      height: 15,
    }
  },

  OddsComparisonButton: {
    minWidth: 62,
    [theme.breakpoints.up(500)]: {
      minWidth: 96,
    },
    [theme.breakpoints.up(768)]: {
      minWidth: 120
    },
    [theme.breakpoints.up(900)]: {
      minWidth: 140
    },
    [theme.breakpoints.up(1440)]: {
      minWidth: 200
    }
  },
  iconButton: {
    padding: 0,
    width: 62,
    height: 15
  },
  drawWithLabel:{
    paddingTop: 15,
    height: 5,
    [theme.breakpoints.up(300)]: {
      paddingTop: 17,
    }
  },
  oddsContainerDraw:{
    height: 10,  
  },
  captionOdd:{
    fontSize: 12,
    padding: '0px 6px',
    '&:nth-last-child(3)': {
      fontSize: 10     
    }
  }
})

export function findCompetitorsWithRecords(competitors: Array<Competitor>, records: Array<Record>): CompetitorsWithMarketSelection {
  return competitors.reduce((result, competitor, index) => {
    const competitorRecords = records && records.find(rec => rec.competitorId === competitor.competitorId)
    const homeOrAway = competitor.homeOrAway || (index ? 'AWAY' : 'HOME')
    result[homeOrAway] = {
      ...competitor,
      records: competitorRecords || {}
    }
    return result
  }, {})
}

export function outcomeValidationByMarket(
  competitors: Array<Competitor>,
  marketType: string,
  selections: Array<MarketSelectionType>
): boolean {
  if (arrayIsNotEmpty(selections) && arrayIsNotEmpty(competitors)) {
    if (marketType === "overUnder") {
      const possibleOutCome = ["Over", "Under"];
      return possibleOutCome.every(outcome => selections.some(selection => selection.outcome === outcome))
    }
    return competitors.every((competitor) => {
      const { competitorId, officialName } = competitor;
      return selections.some(
        (selection) =>
          selection.competitorId === competitorId ||
          selection.name === officialName
      );
    });
  }
}

export function getMarketsToRender(
  markets: {
    [K in string]: Array<Market>
  },
  currentTab: string,
  oddsComparisonBookmakersId: Array<string>,
  competitors: Array<Competitor>
): Array<Market> {
  return (
    markets && markets[currentTab] &&
    oddsComparisonBookmakersId.map((bookmakerId) => {
      const market = markets[currentTab].find(
        (mkt) => mkt.sourceId === bookmakerId
      );
      const isCompetitorsWithSelections =
        market &&
        outcomeValidationByMarket(competitors, currentTab, market.selections);
      return isCompetitorsWithSelections && market;
    }).filter(market => market)
  );
}

export function OddsComparisonComponent(props: PropsComponent) {
  const {
    classes,
    className = '',
    configOptions = {},
    config: {
      oddsComparisonBookmakersId = [],
      isBookmakerLinkEnabled
    } = {},
    data: {
      competitionId,
      competitors,
      fixtureId,
      markets,
      matchSummaries,
      records,
      scheduledStartTime,
      venueName
    } = {},
    translations
  } = props
  const isAwayTeamFirst = !homeFirstCompetitions.includes(competitionId)
  const availableMarkets = markets && Object.keys(markets).filter(key => markets[key] && markets[key].length > 0)
  const [currentTab, setCurrentTab] = useState(availableMarkets && availableMarkets[0])
  const marketsToRender = useMemo(() => {
    return getMarketsToRender(markets, currentTab, oddsComparisonBookmakersId, competitors)
  }, [currentTab, markets])
  const highestPrice = useMemo(() => getHighestPriceInMarkets(marketsToRender), [markets])
  const competitorsWithRecords = competitors && findCompetitorsWithRecords(competitors, records)
  const amountOfMarkets = (markets && Array.isArray(markets[currentTab]) && markets[currentTab].length) || 0
  const linesContainerClass = amountOfMarkets < 3
    ? `${classes.linesContainer} ${classes.linesContainerFewMarkets}` : classes.linesContainer
  const tabElements = markets && availableMarkets.map(market =>
    ({ id: market, name: translations[market] }))
  const matchState = matchSummaries ?
    {
      scoreAway: matchSummaries.awayScore,
      scoreHome: matchSummaries.homeScore,
      phase: matchSummaries.phase,
      matchStatus: matchSummaries.matchStatus
    } : {}

  function onTabChange(tabValue) {
    setCurrentTab(tabValue)
  }
  return (
    <>
      {
        competitors &&
        <Grid
          container
          direction='column'
          wrap='nowrap'
          justifyContent='space-between'
          className={`${classes.root} ${className}`}
        >
          <OddsComparisonHeader
            competitors={competitorsWithRecords}
            isAwayTeamFirst={isAwayTeamFirst}
            scheduledStartTime={scheduledStartTime}
            venueName={venueName}
            matchState={matchState}
            records={records}
          />
          {
            markets && <TabBar
              tabElements={tabElements}
              tabClasses={{
                root: classes.tab,
                fullWidth: classes.tabFullWidth
              }}
              tabBarClasses={{ root: classes.tabBar }}
              appBarClasses={{ root: classes.tabApp }}
              onTabChange={onTabChange}
            />
          }
          <Grid
            alignContent={amountOfMarkets < 3 ? 'center' : 'stretch'}
            container
            justifyContent='space-between'
            className={linesContainerClass}
            data-testid='odds-comparison-market-lines'
          >
            {
              marketsToRender && marketsToRender.map((market) => {
                return (
                  <MarketLine
                    animationKey={fixtureId + currentTab}
                    bookmakers={configOptions.bookmakers}
                    isBookmakerLinkEnabled={isBookmakerLinkEnabled}
                    classes={{
                      root: classes.marketOddLines,
                      iconLink: classes.iconLink,
                      iconLabel: classes.iconLabel,
                      iconButton: classes.iconButton,
                      affiliateText: classes.affiliateText,
                      bestSelection: classes.bestSelection,
                      logo: classes.logo,                      
                      bookmakerContainer: classes.oddsComparisonBookmakerContainer,
                      oddButton: classes.OddsComparisonButton,
                      oddButtonWithDraw: classes.OddsComparisonButton,
                      drawWithLabel: classes.drawWithLabel,
                      oddsContainerDraw: classes.oddsContainerDraw,
                      captionOdd: classes.captionOdd
                    }}
                    competitors={competitors}
                    competitionId={competitionId}
                    marketTypeId={market.marketTypeId}
                    showAffiliateLogo
                    showIcon
                    sourceId={market.sourceId}
                    selections={market.selections}
                    isAwayTeamFirst={isAwayTeamFirst}
                    highestPrice={highestPrice}
                    affiliateLabel='offertext'
                    labelSet='european'
                  />
                )
              }
              )
            }
          </Grid>
        </Grid>
      }
    </>
  )
}

const OddsComparisonComponentComposed = compose(
  withComponentContext,
  withStyles(styles, { name: 'OddsComparisonComponent', defaultTheme: getDefaultTheme }),
  withTranslations((t: TranslationFunction) => {
    return {
      spread: t('oddsComparison.spread.translation', {
        defaultValue: 'Spread',
        context: 'Text to display spread market in OddsComparison widget'
      }),
      moneyLine: t('oddsComparison.moneyLine.translation', {
        defaultValue: 'Money Line',
        context: 'Text to display moneyline market in OddsComparison widget'
      }),
      overUnder: t('oddsComparison.overUnder.translation', {
        defaultValue: 'Totals',
        context: 'Text to display over/under market in OddsComparison widget'
      })
    }
  })
)(OddsComparisonComponent) as React.ElementType

export default OddsComparisonComponentComposed

export const  OddsComparisonComponentWithContext = withComponentContextProvider(OddsComparisonComponentComposed) as React.Component